import React, { useState, useEffect, useCallback } from 'react';
import Chat from '../components/Chat';
import {
    fetchClubs,
    fetchUsers,
    fetchClubInvitations,
    createClub,
    createClubInvite,
    handleClubInvite,
    leaveClub,
} from '../clients/ClubClient';
import { fetchPosts, createPost } from '../clients/PostClient';
import { fetchEvents, createEvent } from '../clients/EventClient';
import { GameVariants, TimeVariants } from '../utils/gameEnums';
import '../Social.css';
import { useNavigate } from 'react-router-dom';
import UnauthorizedMessage from "../components/UnauthorizedMessage";


// TODO: Consider replacing all repeated inline style` props with reusable CSS utility classes.
// TODO: Consider moving Overlay to its own file for reuse
const Overlay = ({ children, onClose, maxWidth = 480 }) => (
    <div className="modal-overlay" onClick={onClose}>
        <div
            className="modal-content"
            style={{ maxWidth }}
            onClick={e => e.stopPropagation()}
        >
            {children}
        </div>
    </div>
);

// TODO: consider moving CreateClubModal into its own file
const CreateClubModal = ({ open, onClose, clubCreateClientFunc, onCreationSuccess }) => {
    const [clubName, setClubName] = useState('');
    const [feedback, setFeedback] = useState({ message: '', type: '' });

    if (!open) return null;

    const handleCreateClub = async () => {
        if (!clubName.trim()) {
            setFeedback({ message: 'Club name cannot be empty.', type: 'error' });
            return;
        }
        setFeedback({ message: '', type: '' });

        try {
            await clubCreateClientFunc(clubName.trim());
            setFeedback({ message: 'Club created successfully!', type: 'success' });
            setClubName('');
            setTimeout(() => {
                setFeedback({ message: '', type: '' }); // Clear success message
                onClose();
                if (onCreationSuccess) onCreationSuccess(); // Trigger refresh in parent
            }, 1500); // Delay to show success message
        } catch (error) {
            setFeedback({ message: error.message || 'Failed to create club.', type: 'error' });

        }
    };

    return (
        <Overlay onClose={onClose}>
            <h3>Create a club</h3>
            <input
                placeholder="Club name"
                value={clubName}
                onChange={e => {
                    setClubName(e.target.value);
                    if (feedback.message) {
                        setFeedback({ message: '', type: '' });
                    }
                }}
                style={{ width: '100%', margin: '10px 0' }}
            />
            {feedback.message && (
                <p style={{
                    color: feedback.type === 'error' ? 'red' : 'green',
                    marginTop: '5px',
                    marginBottom: '10px',
                    fontSize: '0.9em'
                }}>
                    {feedback.message}
                </p>
            )}
            <button disabled={!clubName.trim()} onClick={handleCreateClub}>
                Create
            </button>
        </Overlay>
    );
};

// TODO: consider moving ManageClubModal into its own file
const ManageClubsModal = ({
    open, onClose, members, onInvite, onLeaveClub, activeClub,
}) => {
    const [inviteName, setInviteName] = useState('');
    const [inviteFeedback, setInviteFeedback] = useState({ message: '', type: '' });

    if (!open) return null;

    const handleInviteClick = async () => {
        if (!inviteName.trim()) return;
        setInviteFeedback({ message: '', type: '' });
        try {
            await onInvite(inviteName.trim());
            setInviteFeedback({ message: 'Invite sent successfully!', type: 'success' });
            setInviteName('');
            setTimeout(() => setInviteFeedback({ message: '', type: '' }), 5000); // Clear feedback after 5s
        } catch (error) {
            // ClubClient.createClubInvite is expected to throw an error with a user-facing message.
            setInviteFeedback({
                message: error.message || 'An unexpected error occurred. Please try again.',
                type: 'error'
            });
            setTimeout(() => setInviteFeedback({ message: '', type: '' }), 5000); // Clear feedback after 5s
        }
    };

    return (
        <Overlay onClose={onClose}>
            <h3>{activeClub?.isAdministrator ? 'Administer Club' : 'Club Details'}</h3>

            {activeClub && activeClub.isAdministrator && (
                <>
                    <h4>Invite members</h4>
                    <input
                        placeholder="Username"
                        value={inviteName}
                        onChange={e => {
                            setInviteName(e.target.value);
                            if (inviteFeedback.message) { // Clear feedback when typing
                                setInviteFeedback({ message: '', type: '' });
                            }
                        }}
                        style={{ width: '100%', margin: '6px 0' }}
                    />
                    <button disabled={!inviteName.trim()} onClick={handleInviteClick}>
                        Send invitation
                    </button>
                    {inviteFeedback.message && (
                        <p style={{ color: inviteFeedback.type === 'error' ? 'red' : 'green', marginTop: '10px', fontSize: '0.9em' }}>
                            {inviteFeedback.message}
                        </p>
                    )}
                    <hr style={{ margin: '16px 0' }} />
                </>
            )}

            <h4>Members</h4>
            {members.length === 0
                ? <p>No members.</p>
                : (
                    <ul className="note-list">
                        {members.map(member => (
                            <li key={member.userId} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>
                                    {member.name}
                                    {activeClub && member.userId === activeClub.administratorId && ' (Admin)'}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

            <hr style={{ margin: '20px 0' }} />
            <button
                className="secondary-btn small"
                style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', backgroundColor: '#dc3545', color: 'white' }}
                onClick={onLeaveClub}
            >
                Leave Club
            </button>
        </Overlay>
    );
};

// TODO: Condiser Moving JoinClubModal to its own file
const JoinClubModal = ({
    open, onClose, invites, onAccept, onDecline,
}) => {
    const [loadingInvite, setLoadingInvite] = useState(null); // Stores clubId of invite being processed

    if (!open) return null;

    return (
        <Overlay onClose={() => !loadingInvite && onClose()}> {/* Prevent closing while loading */}
            <h3>Your club-invitations</h3>
            {invites.length === 0
                ? <p className="empty">No pending invitations</p>
                : (
                    <ul className="note-list">
                        {invites.map(invite => (
                            <li key={invite.clubId} style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                                <span style={{ flex: 1 }}>
                                    {invite.clubName} — Invited by {invite.inviter}
                                </span>
                                <button
                                    className="tiny-btn"
                                    disabled={loadingInvite === invite.clubId}
                                    onClick={async () => {
                                        if (loadingInvite) return;
                                        setLoadingInvite(invite.clubId);
                                        try {
                                            await onAccept(invite.clubId);
                                            // onAccept now handles closing the modal
                                        } catch (error) {
                                            alert(`Failed to accept invite: ${error.message || 'Unknown error'}`);
                                        } finally {
                                            setLoadingInvite(null);
                                        }
                                    }}
                                >
                                    {loadingInvite === invite.clubId ? '...' : '✔'}
                                </button>

                                <button
                                    className="tiny-btn"
                                    disabled={loadingInvite === invite.clubId}
                                    onClick={async () => {
                                        if (loadingInvite) return;
                                        setLoadingInvite(invite.clubId);
                                        try {
                                            await onDecline(invite.clubId);
                                            // onDecline now handles closing the modal
                                        } catch (error) {
                                            alert(`Failed to decline invite: ${error.message || 'Unknown error'}`);
                                        } finally {
                                            setLoadingInvite(null);
                                        }
                                    }}
                                >
                                    {loadingInvite === invite.clubId ? '...' : '✖'}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
        </Overlay>
    );
};

//TODO: consider splitting up dayModal such that it doesn't
// both render the notes list and handles creating new events
const DayModal = ({
    open, dateISO, notes, onAdd, onClose,
}) => {
    const [noteText, setNoteText] = useState('');
    if (!open) return null;
    return (
        <Overlay onClose={onClose}>
            <h3>Notes for {dateISO}</h3>

            {notes.length === 0
                ? <p className="empty">No notes yet</p>
                : (
                    <ul className="note-list">
                        {notes.map(note => <li key={note.id}>{note.title}</li>)}
                    </ul>
                )}

            <input
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add note.."
                style={{ width: '100%', margin: '8px 0' }}
            />
            <button disabled={!noteText.trim()}
                onClick={() => { onAdd(noteText.trim()); setNoteText(''); }}>
                Save
            </button>
        </Overlay>
    );
};



const Calendar = ({ events = [], onSelectDate }) => {
    const [currentDate, setCurrentDate] = useState(new Date());


    const year = currentDate.getFullYear();
    const monthIndex = currentDate.getMonth();
    const firstOfMonth = new Date(year, monthIndex, 1);
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();


    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;


    const gridCells = Array.from({ length: 42 }, (_, cellIdx) => {
        const dayNumber = cellIdx - firstWeekday + 1;
        return dayNumber > 0 && dayNumber <= daysInMonth ? dayNumber : null;
    });


    const isoOf = day =>
        `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    const hasEvent = day =>
        day && events.some(ev => ev.date === isoOf(day));

    const isToday = day => {
        if (!day) return false;
        const today = new Date();
        return today.getDate() === day &&
            today.getMonth() === monthIndex &&
            today.getFullYear() === year;
    };

    const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    const weekdayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
        <div className="calendar-widget">
            <div className="cal-header">
                <button onClick={() => setCurrentDate(new Date(year, monthIndex - 1, 1))}>‹</button>
                <span>{monthNames[monthIndex]} {year}</span>
                <button onClick={() => setCurrentDate(new Date(year, monthIndex + 1, 1))}>›</button>
            </div>

            {/* weekday labels */}
            <div className="cal-grid cal-daynames">
                {weekdayNames.map(name => <div key={name}>{name}</div>)}
            </div>

            {/* day cells */}
            <div className="cal-grid">
                {gridCells.map((day, idx) => (
                    <div
                        key={idx}
                        className={
                            `cal-cell${isToday(day) ? ' today' : ''}${hasEvent(day) ? ' has-event' : ''}`
                        }
                        onClick={() => day && onSelectDate(isoOf(day))}
                    >
                        {day && <span className="date-number">{day}</span>}
                        {hasEvent(day) && <span className="event-dot" />}
                    </div>
                ))}
            </div>
        </div>
    );
};

const Social = () => {
    const navigate = useNavigate();
    const [clubs, setClubs] = useState([]);
    const [activeClub, setActiveClub] = useState(null);
    const [clubInvites, setClubInvites] = useState([]);
    const [membersByClub, setMembersByClub] = useState({});
    const [postsByClub, setPostsByClub] = useState({});
    const [eventsByClub, setEventsByClub] = useState({});

    //the part that is commented out will be added again when it can be checked if a person is online
    // const [filterOnlyOnline, setFilterOnlyOnline] = useState(false);
    const [sortMembersByElo, setSortMembersByElo] = useState(false);
    const [socialPageFeedback, setSocialPageFeedback] = useState({ message: '', type: '' });
    const [isUnauthenticated, setIsUnauthenticated] = useState(false);


    const [selectedGameVariant, setSelectedGameVariant] = useState(GameVariants.STANDARD);
    const [selectedTimeVariant, setSelectedTimeVariant] = useState(TimeVariants.RAPID.value);
    const [showSortOptions, setShowSortOptions] = useState(false);

    /* posts */
    const [composerText, setComposerText] = useState('');
    const [showComposer, setShowComposer] = useState(false);


    const [showCreateClub, setShowCreateClub] = useState(false);
    const [showManageClub, setShowManageClub] = useState(false);
    const [showJoinClub, setShowJoinClub] = useState(false);
    const [selectedDayISO, setSelectedDayISO] = useState(null);   // string YYYY-MM-DD

    const [showLeaveConfirmModal, setShowLeaveConfirmModal] = useState(false);
    const [leaveConfirmMessage, setLeaveConfirmMessage] = useState('');
    const [onConfirmLeaveAction, setOnConfirmLeaveAction] = useState(null); // Stores the function to call on confirm


    const loadClubs = useCallback(async () => {
        try {
            const data = await fetchClubs();
            setClubs(data);

            if (activeClub) {
                const currentActiveClubStillExists = data.some(club => club.clubId === activeClub.clubId);
                if (!currentActiveClubStillExists) {
                    setActiveClub(data.length > 0 ? data[0] : null);
                }

            } else if (data.length > 0) {
                setActiveClub(data[0]);
            } else {
                setActiveClub(null);
            }
        } catch (error) {
            if (error.message === "Cannot list clubs - you are not logged in") {

                setIsUnauthenticated(true);
            } else {
                console.error("Failed to load clubs:", error);
            }
            setClubs([]);
            setActiveClub(null);
        }
    }, [activeClub, navigate, setActiveClub, setClubs, setIsUnauthenticated]);

    const loadInvites = useCallback(async () => {
        try {
            const data = await fetchClubInvitations();
            setClubInvites(data);
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {

                setIsUnauthenticated(true);
            } else {
                console.error("Failed to load club invitations:", error);
            }
            setClubInvites([]);
        }
    }, [navigate, setClubInvites, setIsUnauthenticated]);

    const loadMembers = useCallback(async (club) => {
        if (!club || membersByClub[club.clubId]) return;

        const data = await fetchUsers(club.clubName);

        const normalised = data.map(u => ({
            //Dataet is changed such that it fits with existing UI. less lazy fix should be considered
            userId: u.id,
            name: u.username,
            role: u.role,
            elos: u.elos,
            online: u.online,
        }));

        setMembersByClub(prev =>
            ({ ...prev, [club.clubId]: normalised })
        );
    }, [membersByClub]);


    useEffect(() => { loadClubs(); loadInvites(); }, [loadClubs, loadInvites]);

    const confirmedExecuteLeaveClub = async (clubIdToLeave) => {
        try {
            const result = await leaveClub(clubIdToLeave);
            setSocialPageFeedback({ message: result.message || "Successfully processed your request.", type: 'success' });
            setTimeout(() => setSocialPageFeedback({ message: '', type: '' }), 7000);

            setActiveClub(null);
            setMembersByClub(prev => {
                const newState = { ...prev };
                delete newState[clubIdToLeave];
                return newState;
            });
            setPostsByClub(prev => {
                const newState = { ...prev };
                delete newState[clubIdToLeave];
                return newState;
            });
            setEventsByClub(prev => {
                const newState = { ...prev };
                delete newState[clubIdToLeave];
                return newState;
            });
            await loadClubs();
        } catch (error) {
            console.error("Failed to leave club:", error);
            setSocialPageFeedback({
                message: `Failed to leave club: ${error.message || 'Unknown error.'}`,
                type: 'error'
            });
            setTimeout(() => setSocialPageFeedback({ message: '', type: '' }), 7000);
        }
    };

    const handleLeaveClub = () => {
        if (!activeClub) {
            setSocialPageFeedback({ message: "No active club selected to leave.", type: 'error' });
            setTimeout(() => setSocialPageFeedback({ message: '', type: '' }), 7000);
            return;
        }
        setLeaveConfirmMessage(`Are you sure you want to leave ${activeClub.clubName}?`);
        // Store the action to be executed if user confirms in the modal
        const clubIdToActuallyLeave = activeClub.clubId;
        setOnConfirmLeaveAction(() => () => confirmedExecuteLeaveClub(clubIdToActuallyLeave));
        setShowLeaveConfirmModal(true);
    };

    useEffect(() => {
        const oldClubId = activeClub?.clubId; // Capture for potential cleanup if activeClub becomes invalid

        if (!activeClub || !clubs.some(club => club.clubId === activeClub.clubId)) {
            // If activeClub is null OR the user is no longer a member of activeClub
            // (activeClub.clubId not in the main clubs list after clubs state updates)
            // then don't proceed to load members, posts, or events for it.
            // Clear data related to the (now invalid) old activeClub.
            if (oldClubId) { // Check if there was an oldClubId to clean up
                setMembersByClub(prev => {
                    const newState = { ...prev };
                    delete newState[oldClubId];
                    return newState;
                });
                setPostsByClub(prev => {
                    const newState = { ...prev };
                    delete newState[oldClubId];
                    return newState;
                });
                setEventsByClub(prev => {
                    const newState = { ...prev };
                    delete newState[oldClubId];
                    return newState;
                });
            }
            return;
        }


        loadMembers(activeClub);

        fetchPosts(activeClub.clubId).then(posts =>
            setPostsByClub(prev => ({ ...prev, [activeClub.clubId]: posts })));

        fetchEvents(activeClub.clubId).then(events =>
            setEventsByClub(prev => ({
                ...prev,
                [activeClub.clubId]: events.map(ev => ({
                    ...ev,
                    date: ev.event_date.slice(0, 10),   // keep it as ISO string
                    title: ev.title,
                })),
            })));
    }, [activeClub, clubs, loadMembers, setMembersByClub, setPostsByClub, setEventsByClub]);


    const posts = postsByClub[activeClub?.clubId] || [];
    const events = eventsByClub[activeClub?.clubId] || [];

    const eloKey = `${selectedGameVariant}_${selectedTimeVariant}`.toLowerCase();

    const baseline = 1200;                    // default when rating not yet recorded
    const computeElo = m => {                  // because a rating is seemingly not set properly at 800 before
        const rating = m.elos?.[eloKey];        // first game
        return rating !== undefined ? rating : baseline;
    };

    const members =
        (membersByClub[activeClub?.clubId] || [])
            //the part that is commented out will be added again when it can be checked if a person is online
            // .filter(m => !filterOnlyOnline || m.online)
            .sort((a, b) => sortMembersByElo ? computeElo(b) - computeElo(a) : 0);


    const handleNewPost = async text => {
        const saved = await createPost(activeClub.clubId, text);
        setPostsByClub(prev => ({
            ...prev,
            [activeClub.clubId]: [saved, ...prev[activeClub.clubId]],
        }));
    };

    const handleNewEvent = async (isoDate, title) => {
        const saved = await createEvent(activeClub.clubId, title, isoDate);
        setEventsByClub(prev => ({
            ...prev,
            [activeClub.clubId]: [{ id: saved.id, date: isoDate, title }, ...prev[activeClub.clubId]],
        }));
    };


    return (
        <div className="social-page-wrapper"> {/* Wrapper for page and feedback */}
            {socialPageFeedback.message && (
                <div style={{
                    padding: '10px',
                    margin: '10px 20px',
                    backgroundColor: socialPageFeedback.type === 'success' ? '#d4edda' : '#f8d7da',
                    color: socialPageFeedback.type === 'success' ? '#155724' : '#721c24',
                    border: `1px solid ${socialPageFeedback.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                    borderRadius: '4px',
                    textAlign: 'center',
                    position: 'sticky',
                    top: '10px',
                    zIndex: 1050
                }}>
                    {socialPageFeedback.message}
                </div>
            )}
            {isUnauthenticated ? (
                <UnauthorizedMessage/>
            ) : (
                <div className="club-page-container">
                    {/*sidebar */}
                    <aside className="sidebar card-box">
                        <h3 className="sidebar-title">Clubs</h3>
                        <ul className="club-list scrollable">
                            {clubs.map(club => (
                                <li key={club.clubId}
                                    className={club.clubId === activeClub?.clubId ? 'active' : ''}
                                    onClick={() => setActiveClub(club)}>
                                    {club.clubName}
                                </li>
                            ))}
                        </ul>
                        <div className="sidebar-actions">
                            <button className="icon-btn" title="Create club" onClick={() => setShowCreateClub(true)}>＋</button>

                            {activeClub && (
                                <button
                                    className="icon-btn"
                                    title={activeClub.isAdministrator ? "Administer Club" : "View Club / Leave"}
                                    onClick={() => setShowManageClub(true)}
                                >⚙</button>
                            )}
                            <button className="icon-btn" title="Join club" onClick={() => setShowJoinClub(true)}>🔍</button>

                        </div>
                    </aside>

                    {/* main feed */}
                    <main className="main-content card-box">
                        <div className="feed-header" style={{ display: 'flex', gap: '.6rem' }}>
                            {!showComposer && (
                                <button className="primary-btn small" onClick={() => setShowComposer(true)}>
                                    create post
                                </button>
                            )}
                            {showComposer && (
                                <>
                                    <input
                                        className="post-input"
                                        placeholder="Create a post…"
                                        value={composerText}
                                        onChange={e => setComposerText(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && composerText.trim()) {
                                                handleNewPost(composerText.trim());
                                                setComposerText(''); setShowComposer(false);
                                            }
                                            if (e.key === 'Escape') {
                                                setComposerText(''); setShowComposer(false);
                                            }
                                        }}
                                        style={{ flex: 1, padding: '.35rem .55rem' }}
                                    />
                                    <button className="primary-btn small"
                                        disabled={!composerText.trim()}
                                        onClick={() => { handleNewPost(composerText.trim()); setComposerText(''); setShowComposer(false); }}>
                                        Send
                                    </button>
                                    <button className="secondary-btn small" onClick={() => { setComposerText(''); setShowComposer(false); }}>
                                        X
                                    </button>
                                </>
                            )}
                        </div>

                        <section className="posts scrollable">
                            {posts.length === 0
                                ? <p className="empty">No posts yet…</p>
                                : posts.map(post => (
                                    <article key={post.id} className="post-card">
                                        <strong>{post.author}:</strong> {post.text}
                                    </article>
                                ))}
                        </section>
                    </main>

                    {/* calendar */}
                    <aside className="calendar-area card-box">
                        <Calendar
                            events={events}
                            onSelectDate={iso => setSelectedDayISO(iso)}
                        />
                    </aside>

                    {/* chat */}
                    <section className="chat-area card-box">
                        {activeClub && <Chat chatId={activeClub.clubId} chatType={"club"}/>}
                    </section>

                    {/*  members*/}
                    <section className="members-area scrollable">
                        <div className="members-header">
                            <h3>Members ({members.length})</h3>
                            <div className="member-actions">
                                {/* comment out will be reverted when it can be properly checked if a person is online
                        <button
                        className={`pill-btn${filterOnlyOnline ? ' active' : ''}`}
                        onClick={() => setFilterOnlyOnline(prev => !prev)}
                        >
                        {filterOnlyOnline ? 'Show all' : 'Only online'}
                        </button> 
                        */}
                                {/* Sort by ELO dropdown */}
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <button className="pill-btn" onClick={() => setShowSortOptions(prev => !prev)}>
                                        Sort by ELO
                                    </button>
                                    {showSortOptions && (
                                        <div className="card-box sort-dropdown" style={{ position: 'absolute', right: 0, zIndex: 10, padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                                            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '.85rem' }}>
                                                Game variant
                                                <select value={selectedGameVariant} onChange={e => setSelectedGameVariant(e.target.value)}>
                                                    {Object.values(GameVariants).map(gv => (
                                                        <option key={gv} value={gv}>{gv}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '.85rem' }}>
                                                Time format
                                                <select value={selectedTimeVariant} onChange={e => setSelectedTimeVariant(e.target.value)}>
                                                    {Object.values(TimeVariants).map(tv => (
                                                        <option key={tv.value} value={tv.value}>{tv.value}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <button className="primary-btn small" onClick={() => { setSortMembersByElo(true); setShowSortOptions(false); }}>
                                                Apply
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <ul className="member-list">
                            {members.map(member => (
                                <li
                                    key={member.userId}
                                    className={member.online ? 'online' : ''}
                                    onClick={() => navigate(`/profile?username=${member.name}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {member.name}
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/*Modals*/}
                    <CreateClubModal
                        open={showCreateClub}
                        onClose={() => {
                            setShowCreateClub(false);
                            // Potentially clear feedback in modal if it's still there when closing manually
                        }}
                        clubCreateClientFunc={createClub}
                        onCreationSuccess={() => {

                            loadClubs(); // Refresh club list
                        }}
                    />

                    {activeClub && <ManageClubsModal
                        open={showManageClub}
                        onClose={() => setShowManageClub(false)}
                        members={members}
                        onInvite={async (username) => {
                            await createClubInvite(activeClub.clubId, username);
                        }}
                        onLeaveClub={handleLeaveClub}
                        activeClub={activeClub}

                    />}

                    <JoinClubModal
                        open={showJoinClub}
                        onClose={() => setShowJoinClub(false)} // This onClose is for manual closing (e.g., clicking overlay)
                        invites={clubInvites}
                        onAccept={async (clubId) => {
                            // setLoadingInvite will be handled within JoinClubModal for UI indication
                            try {
                                await handleClubInvite(clubId, true);
                                await loadClubs();
                                await loadInvites();
                                setShowJoinClub(false);
                            } catch (error) {
                                console.error("Error accepting club invite:", error);
                                // Re-throw error so JoinClubModal can stop its loading indicator and alert
                                throw error;
                            }
                        }}
                        onDecline={async (clubId) => {
                            try {
                                await handleClubInvite(clubId, false);
                                await loadInvites();
                                setShowJoinClub(false);
                            } catch (error) {
                                console.error("Error declining club invite:", error);
                                throw error; // Re-throw
                            }
                        }}
                    />

                    <DayModal
                        open={Boolean(selectedDayISO)}
                        dateISO={selectedDayISO}
                        notes={events.filter(ev => ev.date === selectedDayISO)}
                        onAdd={title => handleNewEvent(selectedDayISO, title)}
                        onClose={() => setSelectedDayISO(null)}
                    />

                    {/* Inline Confirmation Dialog for Leaving Club */}
                    {showLeaveConfirmModal && (
                        <Overlay onClose={() => {
                            setShowLeaveConfirmModal(false);
                            setOnConfirmLeaveAction(null);
                        }}>
                            <h3>Confirm Leave Club</h3>
                            <p style={{ margin: '20px 0', whiteSpace: 'pre-wrap' }}>
                                {leaveConfirmMessage}
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                <button
                                    type="button"
                                    className="secondary-btn small"
                                    onClick={() => {
                                        setShowLeaveConfirmModal(false);
                                        setOnConfirmLeaveAction(null);
                                    }}
                                >
                                    No, Stay
                                </button>
                                <button
                                    type="button"
                                    className="primary-btn small"
                                    onClick={() => {
                                        if (typeof onConfirmLeaveAction === 'function') {
                                            onConfirmLeaveAction();
                                        }
                                        setShowLeaveConfirmModal(false);
                                        setOnConfirmLeaveAction(null);
                                    }}
                                >
                                    Yes, Leave
                                </button>
                            </div>
                        </Overlay>
                    )}
                </div>
            )}
        </div>
    );
};

export default Social;